import { useEffect, useRef, useState, useCallback } from "react";
import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";
import aiInterviewService from "../../../Services/ai-interview.service";
import { InterviewSessionActivityLog, InterviewSession } from "../../../Interface/Index";

interface UseCameraObstructionDetectionProps {
  videoElement: HTMLVideoElement | null;
  enabled: boolean;
  checkIntervalMs?: number;
  onObstructionDetected?: (reason: string) => void;
}

// Thresholds for detection
const DARK_THRESHOLD = 30;           // Average brightness below this = too dark (blocked by hand/object)
const BRIGHT_THRESHOLD = 240;        // Average brightness above this = too bright (paper covering)
const VARIANCE_THRESHOLD = 100;      // Color variance below this = uniform/covered
const CONSECUTIVE_DETECTIONS = 3;    // Require multiple consecutive detections to avoid false positives

const useCameraObstructionDetection = ({
  videoElement,
  enabled,
  checkIntervalMs = 2000,
  onObstructionDetected,
}: UseCameraObstructionDetectionProps) => {
  const { currentSession, setCurrentSession } = useAIAssessmentContext();
  const [isObstructed, setIsObstructed] = useState(false);
  const [obstructionReason, setObstructionReason] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const consecutiveDetectionsRef = useRef(0);
  const hasLoggedObstructionRef = useRef(false);

  // Helper function to log camera obstruction activity
  const logCameraObstructionActivity = useCallback(async (
    session: InterviewSession
  ) => {
    if (!session) return;

    try {
      // Create activity log entry
      const activity: InterviewSessionActivityLog = {
        event: "camera_covering",
        timestamp: new Date(),
      };

      // Add activity to session
      if (!Array.isArray(session.activityLogs)) {
        session.activityLogs = [];
      }
      session.activityLogs.push(activity);

      // Update session in context
      setCurrentSession({ ...session });

      // Send to API (non-blocking, won't cause termination)
      const response = await aiInterviewService.addActivityLogs(
        session._id,
        session
      );

      // Update session with response if available (but don't handle disqualification)
      if (response?.interviewSession) {
        setCurrentSession(response.interviewSession);
      }
    } catch (error) {
      console.error("[CameraObstructionDetection] Error logging activity:", error);
    }
  }, [setCurrentSession]);

  useEffect(() => {
    if (!enabled || !videoElement) {
      consecutiveDetectionsRef.current = 0;
      hasLoggedObstructionRef.current = false;
      return;
    }

    // Create offscreen canvas for analysis
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) return;

    const analyzeFrame = () => {
      if (!videoElement || videoElement.readyState < 2) return;

      // Set canvas size (use smaller size for performance)
      const sampleWidth = 160;
      const sampleHeight = 120;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      try {
        // Draw current video frame to canvas
        ctx.drawImage(videoElement, 0, 0, sampleWidth, sampleHeight);

        // Get pixel data
        const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
        const pixels = imageData.data;

        // Calculate statistics
        let totalBrightness = 0;
        const brightnessValues: number[] = [];
        const pixelCount = pixels.length / 4;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          // Calculate perceived brightness (human eye weighted)
          const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
          totalBrightness += brightness;
          brightnessValues.push(brightness);
        }

        const avgBrightness = totalBrightness / pixelCount;

        // Calculate variance (how much pixels differ from average)
        let varianceSum = 0;
        for (const brightness of brightnessValues) {
          varianceSum += Math.pow(brightness - avgBrightness, 2);
        }
        const variance = varianceSum / pixelCount;

        // Detect obstruction
        let detectedReason: string | null = null;

        if (avgBrightness < DARK_THRESHOLD) {
          detectedReason = "dark";
        } else if (avgBrightness > BRIGHT_THRESHOLD && variance < VARIANCE_THRESHOLD) {
          detectedReason = "bright";
        } else if (variance < VARIANCE_THRESHOLD) {
          detectedReason = "uniform";
        }

        // Require consecutive detections to avoid false positives
        if (detectedReason) {
          consecutiveDetectionsRef.current++;
          if (consecutiveDetectionsRef.current >= CONSECUTIVE_DETECTIONS) {
            if (!isObstructed) {
              setIsObstructed(true);
              setObstructionReason(detectedReason);
              onObstructionDetected?.(detectedReason);
              
              // Log camera obstruction to activity logs (only once per obstruction event)
              if (currentSession && !hasLoggedObstructionRef.current) {
                hasLoggedObstructionRef.current = true;
                logCameraObstructionActivity(currentSession);
              }
            }
          }
        } else {
          consecutiveDetectionsRef.current = 0;
          hasLoggedObstructionRef.current = false; // Reset when obstruction clears
          if (isObstructed) {
            setIsObstructed(false);
            setObstructionReason(null);
          }
        }
      } catch (error) {
        console.error("[CameraObstructionDetection] Error analyzing frame:", error);
      }
    };

    const intervalId = setInterval(analyzeFrame, checkIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, videoElement, checkIntervalMs, onObstructionDetected, isObstructed, currentSession, logCameraObstructionActivity]);

  const resetObstruction = () => {
    setIsObstructed(false);
    setObstructionReason(null);
    consecutiveDetectionsRef.current = 0;
    hasLoggedObstructionRef.current = false;
  };

  return { isObstructed, obstructionReason, resetObstruction };
};

export default useCameraObstructionDetection;





